/**
 * What has to happen to the cached answers after a request.
 *
 * Every screen caches what it read (react-query), and a write made from one
 * screen changes what many others show: a branch's manager changes the roster
 * grid, the approvals inbox and the branch pickers; an import can change any
 * doctype at all. Asking each save to name every view it affects was tried —
 * page by page — and drifted: the audit of October 2026 found dozens of saves
 * that missed a list, a badge, a picker or a profile tab, and a few that named
 * a key nobody reads.
 *
 * So the rule lives here instead, in one place: any request that changes data
 * marks every cached answer stale. The queries on screen refetch at once;
 * the rest refetch when they are next shown. Saves may still update or
 * invalidate their own keys first — that only makes their own screen faster.
 *
 * Kept free of imports so the unit tests can load it without the bundler.
 */

/**
 * POSTs that only read. They must stay out of the rule, and not only to save
 * requests: several are the query function of a cached query (a report run, a
 * leave preview), and invalidating everything after each of them would
 * refetch that query, which would post again — forever.
 */
const READ_ONLY_POSTS: RegExp[] = [
  // Sign-in, refresh, sign-out and password change manage the session; signing
  // in and out already empty the whole cache.
  /^\/auth\//,
  /^\/reports\/[^/]+\/(run|export)$/,
  /^\/data\/export\//,
  /^\/leave\/applications\/preview$/,
  // The statutory calculators.
  /^\/egypt\/(insurance\/calculate|tax\/annual|tax\/monthly|other-deductions)$/,
];

/** Whether a request that succeeded may have changed what any screen shows. */
export function changesData(method: string | undefined, path: string): boolean {
  const verb = (method ?? 'GET').toUpperCase();
  if (verb === 'GET' || verb === 'HEAD' || verb === 'OPTIONS') return false;
  const bare = path.split('?')[0].replace(/\/+$/, '') || '/';
  return !READ_ONLY_POSTS.some((pattern) => pattern.test(bare));
}

/** Writes this close together are one change: a row-by-row submit loop refreshes once, not per row. */
export const CHANGE_COALESCE_MS = 250;

/**
 * A signal that something changed, coalesced so a save made of several
 * requests (a document and its lines, then submit) refreshes the screen once.
 */
export function createChangeSignal(
  schedule: (run: () => void) => void = (run) => setTimeout(run, CHANGE_COALESCE_MS),
) {
  const listeners = new Set<() => void>();
  let pending = false;
  return {
    notify(): void {
      if (pending) return;
      pending = true;
      schedule(() => {
        pending = false;
        for (const listener of listeners) listener();
      });
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
